import { useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { queryKeyRateId, queryKeyRoomId } from 'constants/url'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { getInitialFlashSameRoomSelection } from 'lib/offer/initialSameRoomSelection'

import { AccommodationRoomSelectionErrorContext } from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationRoomSelectionError'
import AccommodationPackageSelectionLayout from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageSelectionLayout'
import AccommodationPackageSelectionContainer from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageSelectionContainer'

import Caption from 'LuxKit/Typography/Caption'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import AccommodationPackageSelectionPill from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageSelectionPill'
import FilterChip from 'LuxKit/Chips/FilterChip'

import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'

import Carousel from 'LuxKit/Carousel/Carousel'

import { type OfferPageRoom, type FlashSameRoomSelection, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useSuggestedPackageOption from 'hooks/OfferPage/useSuggestedPackageOption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { arePricesLoading, isAvailableForPurchase, getDurationPackageOptions, getIsFlightsSelected } from 'selectors/offerPage/offerPageSelectors'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'

import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { useAppSelector } from 'hooks/reduxHooks'

import usePackageSelectionBurnQuote from 'components/OfferPage/packageSelection/usePackageSelectionBurnQuote'

import { getFlashPackageBurnItems, getFlashCombinedPackageBurnItems } from './flashPackageBurnItems'
import { setOfferPageRooms } from 'contexts/OfferPage/offerPageRoomActions'
import { getOfferPageRoomsTransitionState } from 'selectors/offerPage/roomChangeSelectors'
import useFlashPackageSelectionCapabilities from './useFlashPackageSelectionCapabilities'
import { showFreeCancellation } from 'lib/offer/cancellationPolicyUtils'

import { getUrgencyRoomCounts } from '../getUrgencyRoomCounts'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import FlashPackageSelectionOptions from './FlashPackageSelectionOptions'
import FlashPackageRoom from './FlashPackageRoom'
import { matchesFilter, toggleFilter, type FilterKey } from './flashPackageSelectionFilters'
import FlashPackageSelectionSummary from './FlashPackageSelectionSummary'
import { getUrlFlashPackageSelection, removeInvalidFlashAllocations, resolveFlashPackageSelection, type FlashPackageViewsByOccupancy } from './flashPackageSelection'
import { getFlashPackageViewsByOccupancy } from './flashPackageSelectionSelectors'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'
import { useElementHeight } from 'hooks/useElementSize'
import MultiRoomBookingAssistance from 'components/OfferPage/MultipleRoomsBooking/MultiRoomBookingAssistance'
import { requiresFlashMultiRoomAssistance } from 'lib/offer/flashPurchasePolicy'
import { clearFlashSameRoomSelection, resolveFlashSameRoomSelection } from './flashSameRoomSelection'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'

const Outer = styled.div`
  --offer-page-content-background: ${props => props.theme.palette.neutral.surface.lightBackground};
  --package-selection-mobile-card-width: ${rem(343)};
  background-color: var(--offer-page-content-background);
  padding-block-end: var(--package-mobile-footer-height);
  ${mediaQueryUp.tablet} { padding-block-end: 0; }
`

const Content = styled(AccommodationPackageSelectionContainer)`
  display: grid;
  gap: ${rem(20)};
`

const Filters = styled.div`
  width: 100%;
  max-width: ${rem(1172)};
  margin-inline: auto;
  display: grid;
  align-items: center;
  gap: ${rem(8)};
  padding-block: ${rem(16)};
  padding-inline: ${rem(16)};
  ${mediaQueryUp.tablet} { display: flex; gap: ${rem(12)}; padding-inline: 0; }
`

const FilterChips = styled(Carousel)`
  min-width: 0;
`

const MobileSummaryBooking = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(8)};
`

const EmptyFilters = styled.div`
  display: grid;
  justify-items: start;
  gap: ${rem(12)};
  padding: ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

interface Props {
  isLoading: boolean
  availableForPurchase: boolean
  offer: App.HotelOffer
  rooms: Array<OfferPageRoom>
  sameRoomSelection?: FlashSameRoomSelection
  checkIn?: string
  checkOut?: string
  duration?: number
  packageOptions: Array<App.OfferPackageOptionView>
  viewsByOccupancy: FlashPackageViewsByOccupancy
  canRedeemLuxPlusBenefits: boolean
}

export function FlashPackageSelectionSection({ offer, rooms, sameRoomSelection, checkIn, checkOut, duration, packageOptions, viewsByOccupancy, isLoading, availableForPurchase, canRedeemLuxPlusBenefits }: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const currentContact = useContext(ContactContext)
  const showModal = useContext(ModalContext)
  const callbacksFeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)
  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])
  const dispatch = useOfferPageDispatch()
  const offerPageState = useContext(OfferPageStateContext)
  const roomsTransitionState = getOfferPageRoomsTransitionState(offerPageState)
  const isFlightsSelected = getIsFlightsSelected(offerPageState)
  const urlSearch = useAppSelector(state => state.router.location.search)
  const appliedUrlSelection = useRef(false)
  useEffect(() => {
    if (appliedUrlSelection.current) return
    const params = new URLSearchParams(urlSearch)
    const urlRoomId = params.get(queryKeyRoomId) ?? undefined
    const urlRateId = params.get(queryKeyRateId) ?? undefined
    const room = rooms.length === 1 ? rooms[0] : undefined
    if (!urlRoomId || !urlRateId || !room || room.roomId || room.rateId) {
      appliedUrlSelection.current = true
      return
    }
    const nextRooms = getUrlFlashPackageSelection(rooms, packageOptions, urlRoomId, urlRateId)
    if (!nextRooms) return
    appliedUrlSelection.current = true
    dispatch(setOfferPageRooms(roomsTransitionState, nextRooms))
  }, [dispatch, packageOptions, rooms, roomsTransitionState, urlSearch])
  const suggestedPackage = useSuggestedPackageOption()
  const inclusionsTier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const luxPlusTier = luxPlusEnabled ? inclusionsTier : undefined
  const [activeFilters, setActiveFilters] = useState<Array<FilterKey>>([])
  const togglePopular = useCallback(() => setActiveFilters(current => toggleFilter(current, 'popular')), [])
  const toggleCancellation = useCallback(() => setActiveFilters(current => toggleFilter(current, 'cancellation')), [])
  const toggleUpgrade = useCallback(() => setActiveFilters(current => toggleFilter(current, 'upgrade')), [])
  const toggleBreakfast = useCallback(() => setActiveFilters(current => toggleFilter(current, 'breakfast')), [])
  const clearFilters = useCallback(() => setActiveFilters([]), [])
  const requiresRates = !!offer.property?.useDynamicCancellationPolicies && !!checkIn
  const grouped = useMemo(() => buildRoomPackageOptionsViewOrder(packageOptions), [packageOptions])
  const hasPopular = useMemo(() => packageOptions.some(view => view.package.uniqueKey === suggestedPackage?.option.package.uniqueKey), [packageOptions, suggestedPackage])
  const hasFreeCancellation = useMemo(() => packageOptions.some(view => showFreeCancellation(checkIn, false, view.package.roomRate?.cancellationPolicy)), [checkIn, packageOptions])
  const hasUpgrade = useMemo(() => packageOptions.some(view => !!view.loyaltyUpgradeAvailability), [packageOptions])
  const hasBreakfast = useMemo(() => packageOptions.some(view => matchesFilter(view, ['breakfast'], undefined, checkIn, checkOut, luxPlusTier)), [checkIn, checkOut, luxPlusTier, packageOptions])
  const visibleGroups = useMemo(() => grouped.map(group => ({
    ...group,
    packageViews: group.packageViews.filter(view => matchesFilter(view, activeFilters, suggestedPackage?.option.package.uniqueKey, checkIn, checkOut, luxPlusTier)),
  })).filter(group => group.packageViews.length > 0), [activeFilters, checkIn, checkOut, grouped, luxPlusTier, suggestedPackage])
  const availability = useMemo(() => getUrgencyRoomCounts(packageOptions), [packageOptions])
  const selectionInput = useMemo(() => ({ rooms, viewsByOccupancy, requiresRates, checkInventory: !!checkIn }), [checkIn, requiresRates, rooms, viewsByOccupancy])
  const multipleRooms = rooms.length > 1
  useEffect(() => {
    if (!multipleRooms || sameRoomSelection) return
    const initialSelection = getInitialFlashSameRoomSelection(offer, rooms, duration ?? packageOptions[0]?.package.duration, 'same-room')
    if (initialSelection) dispatch({ type: OfferPageActions.setFlashSameRoomSelection, selection: initialSelection })
  }, [dispatch, duration, multipleRooms, offer, packageOptions, rooms, sameRoomSelection])
  const resolution = useMemo(() => multipleRooms ? undefined : resolveFlashPackageSelection(selectionInput), [multipleRooms, selectionInput])
  const sameRoomResolution = useMemo(() => resolveFlashSameRoomSelection({
    selection: sameRoomSelection,
    offerId: offer.id,
    packageOptions,
    roomCount: rooms.length,
    requiresRates,
    checkInventory: !!checkIn,
    loading: isLoading,
    availableForPurchase,
  }), [availableForPurchase, checkIn, isLoading, offer.id, packageOptions, requiresRates, rooms.length, sameRoomSelection])
  const selectedPackage = multipleRooms ? sameRoomResolution.view : resolution?.allocations[0]?.view
  const complete = multipleRooms ? sameRoomResolution.status === 'valid' : !!resolution?.complete
  const count = selectedPackage ? rooms.length : 0
  const { canAddToCart, canBuyNowBookLater } = useFlashPackageSelectionCapabilities(selectedPackage)
  const showOptions = canAddToCart || canBuyNowBookLater
  const burnItems = useMemo(() => multipleRooms
    ? getFlashCombinedPackageBurnItems(offer, selectedPackage)
    : getFlashPackageBurnItems(offer, resolution?.allocations ?? []), [multipleRooms, offer, resolution?.allocations, selectedPackage])
  const { quote: burnQuote, fetching: burnFetching, ready: burnReady } = usePackageSelectionBurnQuote(burnItems, { registerMaximum: true })
  const burnUnavailable = !burnReady
  const confirmedInvalidSelection = multipleRooms
    ? sameRoomResolution.status === 'unavailable'
    : !!resolution?.issues.some(issue => issue.reason === 'unavailable' || issue.reason === 'stock-exceeded')
  const hasInvalidSelection = !isLoading && offer.packages.length > 0 && packageOptions.length > 0 && confirmedInvalidSelection
  const removeUnavailable = useCallback(() => {
    if (isLoading) return
    if (multipleRooms) dispatch(clearFlashSameRoomSelection(rooms))
    else dispatch(setOfferPageRooms(roomsTransitionState, removeInvalidFlashAllocations(selectionInput)))
  }, [dispatch, isLoading, multipleRooms, rooms, roomsTransitionState, selectionInput])
  const [selectionError, setSelectionError] = useState(false)
  const summaryRef = useRef<HTMLDivElement>(null)
  const mobileSummaryRef = useRef<HTMLDivElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const mobileFooterHeight = useElementHeight(mobileSummaryRef)
  const footerStyle = useMemo<CSSProperties & { '--package-mobile-footer-height': string }>(() => ({
    '--package-mobile-footer-height': rem(mobileFooterHeight ?? 0),
  }), [mobileFooterHeight])
  useAppFooterObserver(mobileSummaryRef, isMobile && count > 0 ? 'sticky' : 'hidden')
  const showInvalid = useCallback(() => {
    setSelectionError(true)
    requestAnimationFrame(() => (isMobile ? mobileSummaryRef : summaryRef).current?.focus())
  }, [isMobile])
  const total = selectedPackage?.hotelTotal ?? 0
  const memberTotal = selectedPackage?.hotelMemberTotal || total
  const hasMemberPrice = !!selectedPackage?.hotelMemberTotal && selectedPackage.hotelMemberTotal < total
  const requiresAssistance = requiresFlashMultiRoomAssistance({ roomCount: rooms.length, canRedeemLuxPlusBenefits, hotelMemberTotal: selectedPackage?.hotelMemberTotal ?? 0 })
  const bookNowLabel = intl.formatMessage({ id: 'mpFHvG15p8mwo5wMs8b2', defaultMessage: 'Book now', description: 'Call-to-action button text to proceed with booking the current offer' })
  const checkAvailabilityLabel = intl.formatMessage({ id: '_etWrwIdpiw96KvQ_Pzp', defaultMessage: 'Check availability', description: 'Button text to check availability on the hotel or room booking page' })
  const undated = !checkIn
  const primaryLabel = undated ? checkAvailabilityLabel : bookNowLabel
  const openDateSelection = useCallback(() => {
    dispatch({ type: OfferPageActions.checkAvailability, filter: 'checkin', flights: isFlightsSelected })
  }, [dispatch, isFlightsSelected])
  const popularLabel = intl.formatMessage({
    id: 'NBs9cPAlXqUlCdsLFF52',
    defaultMessage: 'Popular',
    description: 'Generic recommendation filter for the Flash packages, independent of a particular guest room’s occupancy',
  })
  const onRemove = useCallback(() => {
    if (multipleRooms) dispatch(clearFlashSameRoomSelection(rooms))
    else dispatch(setOfferPageRooms(roomsTransitionState, rooms.map(room => ({ ...room, roomId: undefined, rateId: undefined }))))
  }, [dispatch, multipleRooms, rooms, roomsTransitionState])
  const selectedChips = selectedPackage && <AccommodationPackageSelectionPill
    count={count}
    packageName={selectedPackage.package.name}
    onRemove={onRemove}
  />
  const summary = <FlashPackageSelectionSummary
    ref={summaryRef}
    presentation="rail"
    count={count}
    price={total}
    memberPrice={memberTotal}
    hasMemberPrice={hasMemberPrice}
    offerConfig={offer.luxPlus}
    burnPayment={burnQuote}
    loading={isLoading || burnFetching}
    selection={selectedChips}
    actions={<>
      {requiresAssistance && <MultiRoomBookingAssistance currentRegionCode={currentRegionCode} phoneNumber={currentContact.defaultContact.number} phoneLabel={currentContact.defaultContact.humanReadable} callbacksFeatureEnabled={callbacksFeatureEnabled} onCallbackButtonClick={onCallbackButtonClick} />}
      {selectedPackage && !requiresAssistance && <OfferBookButton disabled={burnUnavailable} requireCommittedSameRoomSelection={multipleRooms} packageView={selectedPackage} size="medium" fit="full-width">{primaryLabel}</OfferBookButton>}
      {!selectedPackage && <TextButton kind="primary" size="medium" fit="full-width" onClick={undated ? openDateSelection : showInvalid} disabled={isLoading}>{primaryLabel}</TextButton>}
      {showOptions && <FlashPackageSelectionOptions disabled={burnUnavailable} packageView={selectedPackage} onInvalidSelection={showInvalid} />}
    </>}
  />

  return <AccommodationRoomSelectionErrorContext.Provider value={selectionError && !complete}>
    <Outer data-testid="flash-option-d" style={footerStyle}>
      <Content>
        {hasInvalidSelection && <MessageBanner kind="critical">
          <VerticalSpacer gap={8}>
            {intl.formatMessage({
              id: 'o-DebUVP6HyvDkdrA4Fl',
              defaultMessage: 'Some selected packages are no longer available for your rooms. Review your selection to continue.',
              description: 'Explains a Flash package or its room inventory became unavailable while preserving the visitor’s guests and valid choices',
            })}
            <TextLink size="small" onClick={removeUnavailable}>{intl.formatMessage({
              id: 'PwwrRjY6jzlkixC8Z_MY',
              defaultMessage: 'Remove unavailable selections',
              description: 'Clears only invalid Flash room package assignments while preserving all requested guests and remaining valid packages',
            })}
            </TextLink>
          </VerticalSpacer>
        </MessageBanner>}
        {(hasPopular || hasFreeCancellation || hasUpgrade || hasBreakfast || activeFilters.length > 0) && <Filters>
          <Caption variant="large" weight="bold">{intl.formatMessage({ id: 'HlYFHW8cQtZ_EK_-L8W3', defaultMessage: 'Filter packages', description: 'Heading for filters above the Flash hotel room package selection' })}</Caption>
          <FilterChips gutterStyle="overflow" gap={8}>
            {(hasPopular || activeFilters.includes('popular')) && <FilterChip size="medium" selected={activeFilters.includes('popular')} startIcon={<LineUsersAltIcon />} onClick={togglePopular}>{popularLabel}</FilterChip>}
            {(hasFreeCancellation || activeFilters.includes('cancellation')) && <FilterChip size="medium" selected={activeFilters.includes('cancellation')} startIcon={<LineShieldCheckIcon />} onClick={toggleCancellation}>{intl.formatMessage({ id: 'Cnk7NVK8VWdi1ubUWdxY', defaultMessage: 'Free cancellation', description: 'Label for togglable filter chip that restricts displayed hotel rates to those with free cancellation' })}</FilterChip>}
            {(hasUpgrade || activeFilters.includes('upgrade')) && <FilterChip size="medium" selected={activeFilters.includes('upgrade')} startIcon={<LineCrownIcon />} onClick={toggleUpgrade}>{intl.formatMessage({ id: 'fjiROZeh0Z0JL4SokuEh', defaultMessage: 'Free upgrade', description: 'Filter chip for Flash hotel packages with an available free loyalty upgrade' })}</FilterChip>}
            {(hasBreakfast || activeFilters.includes('breakfast')) && <FilterChip size="medium" selected={activeFilters.includes('breakfast')} startIcon={<LineCoffeeIcon />} onClick={toggleBreakfast}>{intl.formatMessage({ id: 'i3sW2TdGVkQlV1SDgZxY', defaultMessage: 'Breakfast', description: 'Filter chip for Flash hotel packages including breakfast' })}</FilterChip>}
          </FilterChips>
        </Filters>}
        <AccommodationPackageSelectionLayout summary={summary}>
          {visibleGroups.length === 0 && activeFilters.length > 0 && <EmptyFilters>
            <Caption variant="large">{intl.formatMessage({ id: 'tqwQ3uP_ASsb7_8VeACq', defaultMessage: 'No packages match these filters.', description: 'Empty state when selected Flash hotel package filters exclude all currently available packages' })}</Caption>
            <TextButton kind="secondary" size="small" onClick={clearFilters}>{intl.formatMessage({ id: 'B4gh_yr8Sd-nTOtStSZn', defaultMessage: 'Clear filters', description: 'Action to reset Flash hotel package filters after the current combination has no results' })}</TextButton>
          </EmptyFilters>}
          {visibleGroups.map(group => <FlashPackageRoom
            offer={offer}
            checkIn={checkIn}
            isLoading={isLoading}
            availableForPurchase={availableForPurchase}
            key={group.roomId}
            views={group.packageViews}
            availableRoomCount={availability.get(group.roomId)}
            singleRoomSelection={resolution}
            singleRoomBurnQuote={burnQuote}
            burnUnavailable={burnUnavailable}
            viewsByOccupancy={viewsByOccupancy}
            rooms={rooms}
            sameRoomSelection={sameRoomSelection}
            selectedRoomTypeId={selectedPackage?.package.roomType.id}
            requiresRates={requiresRates}
            suggestedPackage={suggestedPackage}
          />)}
        </AccommodationPackageSelectionLayout>
        <FlashPackageSelectionSummary
          ref={mobileSummaryRef}
          presentation="mobile-footer"
          count={count}
          price={total}
          memberPrice={memberTotal}
          hasMemberPrice={hasMemberPrice}
          offerConfig={offer.luxPlus}
          burnPayment={burnQuote}
          loading={isLoading || burnFetching}
          duration={selectedPackage?.package.duration}
          checkIn={checkIn}
          actions={<>
            {requiresAssistance && <MultiRoomBookingAssistance currentRegionCode={currentRegionCode} phoneNumber={currentContact.defaultContact.number} phoneLabel={currentContact.defaultContact.humanReadable} callbacksFeatureEnabled={callbacksFeatureEnabled} onCallbackButtonClick={onCallbackButtonClick} />}
            {!requiresAssistance && <MobileSummaryBooking>
              {selectedPackage && <OfferBookButton disabled={burnUnavailable} requireCommittedSameRoomSelection={multipleRooms} packageView={selectedPackage} size="small" fit="full-width">{primaryLabel}</OfferBookButton>}
              {!selectedPackage && <TextButton kind="primary" size="small" fit="full-width" onClick={undated ? openDateSelection : showInvalid} disabled={isLoading}>{primaryLabel}</TextButton>}
              {showOptions && <FlashPackageSelectionOptions disabled={burnUnavailable} packageView={selectedPackage} onInvalidSelection={showInvalid} presentation="icon" size="small" />}
            </MobileSummaryBooking>}
          </>}
        />
      </Content>
    </Outer>
  </AccommodationRoomSelectionErrorContext.Provider>
}

export default withOfferPageAndReduxState((state, appState): Props => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms,
  sameRoomSelection: state.flashSameRoomSelection,
  isLoading: arePricesLoading(appState, state),
  availableForPurchase: isAvailableForPurchase(state, appState),
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  packageOptions: getDurationPackageOptions(state, appState),
  viewsByOccupancy: getFlashPackageViewsByOccupancy(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
}), FlashPackageSelectionSection)
